import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css"; //if using mantine date picker features
import "mantine-react-table/styles.css"; //make sure MRT styles were imported in your app root (once)
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Controller, useForm as useFormReactHook } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Users,
  Plus,
  Trash2,
  Upload,
  Download,
  UserPlus,
  AlertTriangle,
  Settings,
  ChevronDown,
  ChevronRight,
  Edit,
  GraduationCap,
  TrendingUp,
  UserCheck,
  UserMinus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { UnifiedLoadingSpinner } from "@/components/loading/UnifiedLoadingSpinner";
import { TabsContent } from "@/components/ui/tabs";
import {
  ActionIcon,
  Badge,
  createTheme,
  DirectionProvider,
  Divider,
  Flex,
  Loader,
  MantineProvider,
  Modal,
  MultiSelect,
  Stack,
  Table,
  Title,
} from "@mantine/core";
import {
  flexRender,
  MantineReactTable,
  MRT_ColumnDef,
  MRT_GlobalFilterTextInput,
  MRT_TableBodyCellValue,
  MRT_ToolbarAlertBanner,
  useMantineReactTable,
} from "mantine-react-table";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "@mantine/form";
import { se } from "date-fns/locale";
import { ArabicCalendar } from "@/components/ui/arabic-calendar";
import { dateSplitter } from "@/utils/formatting/deteSplitter";
import { toArabicNumbers } from "@/utils/arabicNumbers";
import { getDaysDifference } from "@/utils/formatting/dateFormatters";

interface teacherForm {
  full_name: string;
  email: string;
  sections: string[];
}

const StudentAttendance = () => {
  const { user, userProfile } = useAuth();
  const [attendances, setAttendances] = useState<any[]>([]);

  const [isLoading, setLoading] = useState(true);

  const startInitDate = new Date();
  startInitDate.setDate(startInitDate.getDate() - 7);
  const [startDate, setStartDate] = useState(dateSplitter(startInitDate));
  const [endDate, setEndDate] = useState(dateSplitter(new Date()));

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("attendance_records")
        .select("*")
        .eq("student_id", userProfile?.student_id)
        .lte("date", endDate)
        .gte("date", startDate);
      if (error) {
        // handle error (toast, etc.)
        setAttendances([]);
      } else {
        setAttendances(data || []);
      }
      setLoading(false);
    };
    if (userProfile.student_id) fetchAttendance();
  }, [userProfile.student_id, endDate, startDate]);

  const columns: MRT_ColumnDef<any>[] = [
    {
      // accessorKey: "full_name",
      header: "#",
      Cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "date",
      header: "التأريخ",
      Cell: ({ row }) =>
        new Date(row.original?.date).toLocaleDateString("ar-SA"),
    },
    {
      accessorKey: "status",
      header: "الحالة",
      Cell: ({ cell, row }) => {
        const status = cell.getValue<any>();
        const colorsMap = {
          present: "green",
          absent: "red",
          absent_with_reason: "orange",
        };
        const color = status ? colorsMap[status] : "gray";

        return (
          <div className="flex flex-col gap-1">
            <Badge variant="light" color={color} radius="md" size="lg">
              {!status && "غير محدد"}
              {status === "present" && "حاضر"}
              {status === "absent" && "غائب"}
              {status === "absent_with_reason" && "غائب بعذر"}
            </Badge>
          </div>
        );
      },
    },

    {
      accessorKey: "absent_reason",
      header: "سبب الغياب",
    },
  ];
  const table = useMantineReactTable({
    data: attendances,
    columns,
    mantineTableProps: {
      striped: false,
      border: 0,
    },
    mantinePaperProps: {
      shadow: "none", //use a larger shadow
      //customize paper styles
    },
    // enableRowSelection: true,
    initialState: {
      pagination: { pageSize: 20, pageIndex: 0 },
      showGlobalFilter: true,
    },
    //customize the MRT components
    mantinePaginationProps: {
      rowsPerPageOptions: ["5", "10", "15"],
    },
    paginationDisplayMode: "pages",
    state: {
      isLoading: isLoading,
    },
    enableGlobalFilter: false,
  });

  const stats = useMemo(() => {
    const totalDays = getDaysDifference(startDate, endDate);
    const total = attendances.length;
    let present = 0;
    let absent = 0;
    let withResoan = 0;

    attendances.forEach((atte) => {
      const status = atte.status;
      if (status === "present") present++;
      else if (status === "absent") absent++;
      else if (status === "absent_with_reason") withResoan++;
    });

    return {
      total,
      totalDays,
      present,
      absent,
      notMarked: total - present - absent - withResoan,
      withReason: withResoan,
    };
  }, [attendances, startDate, endDate]);

  return (
    <Card className="bg-white/70 backdrop-blur-sm border-white/20" dir="rtl">
      <CardHeader className="bg-secondary text-white rounded-t-lg">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-white text-lg">
              <Users className="h-5 w-5 text-white" />
              قائمة الحضور والغياب
            </CardTitle>
            {/* <CardDescription className="text-sm text-white/90">
                إدارة بيانات الطلاب المسجلين
              </CardDescription> */}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="bg-secondary/5 p-4 rounded-lg border border-secondary/10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label
                htmlFor="startdate"
                className="text-sm font-medium text-secondary block mb-2"
              >
                تأريخ البداية
              </Label>
              <ArabicCalendar
                value={startDate ? new Date(startDate) : undefined}
                onChange={(date) => setStartDate(dateSplitter(date))}
                placeholder="اختر التاريخ"
                className="w-full"
              />
            </div>{" "}
            <div>
              <Label
                htmlFor="enddate"
                className="text-sm font-medium text-secondary block mb-2"
              >
                تأريخ البداية
              </Label>
              <ArabicCalendar
                value={endDate ? new Date(endDate) : undefined}
                onChange={(date) => setEndDate(dateSplitter(date))}
                placeholder="اختر التاريخ"
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 mt-8 mx-4">
          {/* إجمالي الطلاب */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-emerald-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  الأيام المحددة{" "}
                </p>
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-emerald-600">
                  {toArabicNumbers(stats.totalDays.toString())}
                </p>
              </div>
            </div>
          </div>

          {/* معدل الحضور */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-cyan-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  معدل الحضور
                </p>
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-cyan-600">
                  {stats.total > 0
                    ? toArabicNumbers(
                        Math.round(
                          (stats.present / stats.total) * 100
                        ).toString()
                      )
                    : toArabicNumbers("0")}
                  %
                </p>
              </div>
            </div>
          </div>

          {/* الحاضرون */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-green-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  حاضر
                </p>
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-green-600">
                  {toArabicNumbers(stats.present.toString())}
                </p>
              </div>
            </div>
          </div>

          {/* الغائبون */}
          <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-red-500 min-h-[60px]">
            <div className="flex items-center gap-2 w-full">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <UserMinus className="w-4 h-4 text-red-600" />
              </div>
              <div className="flex-1 text-center">
                <p className="text-gray-600 text-xs font-medium whitespace-nowrap">
                  غائب
                </p>
                <div>
                  <span className="text-xs text-foreground/70 font-medium">
                    غائب بعذر :
                  </span>
                  <span className="text-sm text-foreground/70 font-medium">
                    {toArabicNumbers(stats.withReason.toString())}
                  </span>{" "}
                </div>{" "}
              </div>
              <div className="flex-shrink-0">
                <p className="text-lg font-bold text-red-600">
                  {toArabicNumbers(
                    (stats.absent + stats.withReason).toString()
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
        <Stack>
          <MantineReactTable table={table} />
        </Stack>
      </CardContent>
    </Card>
  );
};
export default StudentAttendance;
